/**
 * PRP-MCP-15: the signed-in user's estimate headings (company name, code, address,
 * logo, opening and closing statements, estimator contact), several per user. None
 * applies unless one is the default; each estimate chooses its own and may change
 * its statements or estimator for itself only.
 *
 * Contract: app/api/routes/company_profile.py, mounted under /api/v1/me.
 * Statements travel as plain text; the server keeps imported RTF when the text
 * comes back unchanged. Failures arrive as `ApiError` (message = backend `detail`).
 */
import { api } from './api';
import type { HeadingStyle } from '../types/headingStyle';

export interface CompanyEstimator {
  name?: string;
  adj_no?: string;
  qcode?: string;
  company_name?: string;
  company_qcode?: string;
  street?: string;
  city?: string;
  state?: string;
  postal?: string;
  phone?: string;
  email?: string;
}

export interface HeadingFields {
  company_name: string | null;
  company_code: string | null;
  header_info: string | null;
  opening_statement: string | null;
  closing_statement: string | null;
  estimator: CompanyEstimator | null;
  /** data: URL of the logo, or null. */
  logo: string | null;
  source: 'esx_import' | 'manual' | null;
  source_file: string | null;
  updated_at: string | null;
}

export interface Heading extends HeadingFields {
  id: string;
  name: string;
  is_default: boolean;
  /** This company's section-heading style; null = the account style (Settings, Scope structure). */
  heading_style: HeadingStyle | null;
}

/** What a save sends: only the fields that changed. */
export type HeadingUpdate = Partial<{
  name: string;
  company_name: string;
  company_code: string;
  header_info: string;
  opening_statement: string;
  closing_statement: string;
  estimator: CompanyEstimator;
  heading_style: HeadingStyle | null;
}>;

/** An estimate's own statements/estimator (F4). Empty text clears one. */
export type HeadingOverrides = Partial<{
  opening_statement: string;
  closing_statement: string;
  estimator: CompanyEstimator;
}>;

/** "none" = no heading; null = follow the default. */
export type HeadingChoice = string | null;

export interface EstimateHeading {
  heading_id: HeadingChoice;
  /** chosen | default (follows the default) | none | missing (the chosen heading was deleted) */
  state: 'chosen' | 'default' | 'none' | 'missing';
  heading: { id: string; name: string } | null;
  overrides: { opening_statement: string | null; closing_statement: string | null; estimator: CompanyEstimator | null };
  /** The heading as this estimate exports it, overrides on top. */
  effective: HeadingFields | null;
}

export const NO_HEADING = 'none';

export const HeadingsAPI = {
  list: async (): Promise<{ headings: Heading[]; limit: number }> => api.get('/me/headings'),

  create: async (body: HeadingUpdate): Promise<{ heading: Heading }> => api.post('/me/headings', body),

  update: async (id: string, body: HeadingUpdate): Promise<{ heading: Heading }> =>
    api.put(`/me/headings/${encodeURIComponent(id)}`, body),

  remove: async (id: string): Promise<{ deleted: boolean }> => api.delete(`/me/headings/${encodeURIComponent(id)}`),

  setDefault: async (id: string | null): Promise<{ default: Heading | null }> =>
    api.post('/me/headings/default', { heading_id: id }),

  /** Read a heading out of one of the company's own ESX files, into a new heading or `intoId`. */
  importEsx: async (file: File, intoId?: string): Promise<{ heading: Heading; imported: string[] }> => {
    const formData = new FormData();
    formData.append('file', file);
    if (intoId) formData.append('heading_id', intoId);
    return api.post('/me/headings/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },

  forEstimate: async (parseHash: string): Promise<EstimateHeading> =>
    api.get(`/me/headings/estimate/${encodeURIComponent(parseHash)}`),

  setForEstimate: async (
    parseHash: string,
    body: { heading_id?: HeadingChoice; overrides?: HeadingOverrides | null },
  ): Promise<EstimateHeading> => api.put(`/me/headings/estimate/${encodeURIComponent(parseHash)}`, body),

  saveToHeading: async (parseHash: string): Promise<EstimateHeading> =>
    api.post(`/me/headings/estimate/${encodeURIComponent(parseHash)}/save-to-heading`, {}),
};
